import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Upload, message, Typography, Space, Carousel, Avatar, List, Popconfirm, Divider } from 'antd';
import { PictureOutlined, UploadOutlined, HeartOutlined, HeartFilled, MessageOutlined, DeleteOutlined, UserOutlined, SendOutlined } from '@ant-design/icons';
import { FeedService } from '../api/services/feed.service';
import { useAuth } from '../store/auth';
import SmartMentionsInput from '../components/SmartMentionsInput';
import LinkifyText from '../components/LinkifyText';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import ArchiveMedia from '../components/ArchiveMedia';

dayjs.extend(relativeTime);
const { Title, Text } = Typography;

export default function FeedPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [activeCommentPostId, setActiveCommentPostId] = useState(null);
  const [commentText, setCommentText] = useState('');
  const { user } = useAuth();

  const fetchFeed = async () => {
    setLoading(true);
    try {
      const res = await FeedService.getFeed({ limit: 50 });
      setPosts(res.data);
    } catch (e) {
      message.error('Ошибка загрузки ленты');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeed();
  }, []);

  const handleCreatePost = async (values) => {
    if (!values.content && (!values.media || values.media.length === 0)) {
      message.error('Пост не может быть пустым');
      return;
    }

    try {
      const res = await FeedService.createPost({ content: values.content });
      const postId = res.data.id;

      if (values.media && values.media.length > 0) {
        for (const fileItem of values.media) {
          const fileObj = fileItem.originFileObj;
          if (fileObj) {
            const formData = new FormData();
            formData.append('file', fileObj);
            try {
              await FeedService.uploadMedia(postId, formData);
            } catch (err) {
              message.error(`Ошибка загрузки файла ${fileObj.name}`);
            }
          }
        }
      }

      message.success('Пост опубликован!');
      form.resetFields();
      fetchFeed();
    } catch (e) {
      message.error('Ошибка публикации поста');
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      await FeedService.toggleLike(postId);
      setPosts(prev => prev.map(p => {
        if (p.id === postId) {
          return {
            ...p,
            like_count: p.is_liked_by_me ? p.like_count - 1 : p.like_count + 1,
            is_liked_by_me: !p.is_liked_by_me
          };
        }
        return p;
      }));
    } catch (e) {
      message.error('Ошибка');
    }
  };

  const handleAddComment = async (postId) => {
    if (!commentText.trim()) return;
    
    const textToSubmit = commentText;
    setCommentText('');
    setActiveCommentPostId(null);
    
    try {
      const res = await FeedService.addComment(postId, { content: textToSubmit });
      setPosts(prev => prev.map(p => {
        if (p.id === postId) {
          return { ...p, comments: [...p.comments, res.data] };
        }
        return p;
      }));
    } catch (e) {
      setCommentText(textToSubmit);
      setActiveCommentPostId(postId);
      message.error('Ошибка добавления комментария');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await FeedService.deletePost(postId);
      message.success('Пост удален');
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (e) {
      message.error('Ошибка удаления поста');
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e?.fileList;
  };

  const avatarUrl = (avatarId) => avatarId ? `/api/v1/files/${avatarId}/content` : null;

  return (
    <div className="page-container" style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: 20,
          }}>
            <PictureOutlined />
          </div>
          Лента ТЕРРА
        </h1>
        <p className="page-subtitle">Делитесь новостями и идеями</p>
      </div>

      <Card 
        style={{ marginBottom: 24, borderRadius: 16 }}
        styles={{ body: { padding: 20 } }}
      >
        <Form form={form} layout="vertical" onFinish={handleCreatePost}>
          <Form.Item name="content" style={{ marginBottom: 16 }}>
            <SmartMentionsInput rows={3} placeholder="Что у вас нового?" />
          </Form.Item>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Form.Item name="media" valuePropName="fileList" getValueFromEvent={normFile} style={{ marginBottom: 0 }}>
              <Upload beforeUpload={() => false} multiple listType="picture" accept="image/*,video/*">
                <Button icon={<UploadOutlined />}>Фото/видео</Button>
              </Upload>
            </Form.Item>
            <Button 
              type="primary" 
              htmlType="submit"
              style={{ 
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                border: 'none',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
              }}
            >
              Опубликовать
            </Button>
          </div>
        </Form>
      </Card>

      <List
        loading={loading}
        dataSource={posts}
        renderItem={post => {
          const canDelete = post.author_id === user?.id || (user?.role_level >= 5 || user?.username === 'khaminov');

          return (
            <Card 
              style={{ marginBottom: 20, borderRadius: 16, overflow: 'hidden' }}
              styles={{ body: { padding: 0 } }}
              className="card-hover"
            >
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <Avatar 
                    src={avatarUrl(post.author_avatar_id)} 
                    icon={<UserOutlined />} 
                    style={{ 
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      width: 40,
                      height: 40,
                    }} 
                  />
                  <div>
                    <Text strong style={{ display: 'block', fontSize: 14 }}>{post.author_name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(post.created_at).fromNow()}</Text>
                  </div>
                </Space>
                {canDelete && (
                  <Popconfirm title="Удалить пост?" onConfirm={() => handleDeletePost(post.id)}>
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </div>

              {post.media && post.media.length > 0 && (
                <div style={{ background: '#0f172a' }}>
                  {post.media.length > 1 ? (
                    <Carousel arrows={true}>
                      {post.media.map(m => (
                        <div key={m.id} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {m.media_type === 'video' ? (
                            <ArchiveMedia kind="video" src={`/api/v1/files/${m.file_id}/content`} style={{ maxHeight: 500, maxWidth: '100%', objectFit: 'contain', margin: '0 auto', display: 'block' }} />
                          ) : (
                            <ArchiveMedia src={`/api/v1/files/${m.file_id}/content`} style={{ maxHeight: 500, maxWidth: '100%', objectFit: 'contain', margin: '0 auto', display: 'block' }} alt="media" />
                          )}
                        </div>
                      ))}
                    </Carousel>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {post.media[0].media_type === 'video' ? (
                        <ArchiveMedia kind="video" src={`/api/v1/files/${post.media[0].file_id}/content`} style={{ maxHeight: 500, maxWidth: '100%', objectFit: 'contain', margin: '0 auto', display: 'block' }} />
                      ) : (
                        <ArchiveMedia src={`/api/v1/files/${post.media[0].file_id}/content`} style={{ maxHeight: 500, maxWidth: '100%', objectFit: 'contain', margin: '0 auto', display: 'block' }} alt="media" />
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={{ padding: '16px 20px' }}>
                <Space size="large" style={{ marginBottom: 12 }}>
                  <span 
                    style={{ 
                      cursor: 'pointer', 
                      fontSize: 22, 
                      color: post.is_liked_by_me ? '#ef4444' : '#94a3b8',
                      transition: 'all 0.2s',
                    }} 
                    onClick={() => handleToggleLike(post.id)}
                  >
                    {post.is_liked_by_me ? <HeartFilled /> : <HeartOutlined />}
                  </span>
                  <span 
                    style={{ cursor: 'pointer', fontSize: 22, color: '#94a3b8' }} 
                    onClick={() => setActiveCommentPostId(activeCommentPostId === post.id ? null : post.id)}
                  >
                    <MessageOutlined />
                  </span>
                </Space>
                
                {post.like_count > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Text strong style={{ fontSize: 13 }}>{post.like_count} отметок "Нравится"</Text>
                  </div>
                )}

                {post.content && (
                  <div style={{ marginBottom: 12, lineHeight: 1.6 }}>
                    <Text strong style={{ marginRight: 8 }}>{post.author_name}</Text>
                    <LinkifyText text={post.content} />
                  </div>
                )}

                {post.comments && post.comments.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Text 
                      type="secondary" 
                      style={{ cursor: 'pointer', fontSize: 13 }} 
                      onClick={() => setActiveCommentPostId(post.id)}
                    >
                      {post.comments.length === 1 ? '1 комментарий' : `${post.comments.length} комментариев`}
                    </Text>
                    
                    {(activeCommentPostId === post.id ? post.comments : post.comments.slice(-2)).map(c => (
                      <div key={c.id} style={{ marginTop: 8, padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                        <Text strong style={{ marginRight: 8, fontSize: 13 }}>{c.author_name}</Text>
                        <LinkifyText text={c.content} />
                      </div>
                    ))}
                  </div>
                )}

                {activeCommentPostId === post.id && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                       <SmartMentionsInput 
                          placeholder="Добавьте комментарий..." 
                          value={commentText} 
                          onChange={(val) => setCommentText(val)} 
                          onSubmit={() => handleAddComment(post.id)}
                          autoSize={{ minRows: 1, maxRows: 4 }}
                       />
                    </div>
                    <Button 
                      type="link" 
                      onClick={() => handleAddComment(post.id)}
                      style={{ color: '#6366f1', fontWeight: 600 }}
                    >
                      Отправить
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          );
        }}
      />
    </div>
  );
}
